import type { ReactNode, SelectHTMLAttributes } from 'react'
import { forwardRef } from 'react'
import { Field } from './Field'
import { ChevronDownIcon } from './icons'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'children'> {
  label?: ReactNode
  hideLabel?: boolean
  hint?: ReactNode
  error?: ReactNode
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  /** Shown as a disabled first row when `value` is ''. */
  placeholder?: string
  fieldClassName?: string
}

/** Native select on purpose: the platform picker is the best one-handed picker there is. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hideLabel,
    hint,
    error,
    value,
    onChange,
    options,
    placeholder,
    className,
    fieldClassName,
    ...rest
  },
  ref,
) {
  return (
    <Field label={label} hideLabel={hideLabel} hint={hint} error={error} className={fieldClassName}>
      {({ id, describedBy, invalid }) => (
        <span className="rs-select-wrap">
          <select
            ref={ref}
            id={id}
            className={`rs-input rs-select ${invalid ? 'rs-input--invalid' : ''} ${className ?? ''}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            {...rest}
          >
            {placeholder !== undefined ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="rs-select-wrap__chev" aria-hidden="true">
            <ChevronDownIcon size={18} />
          </span>
        </span>
      )}
    </Field>
  )
})
